import type { Lesson } from './types.js';

// =============================================================================
// SECTION 3 — Conversational (B1). Themes: Marvel, Dark, Peaky Blinders,
// Attack on Titan, Black Mirror.
// =============================================================================

export const U11_LESSONS: Lesson[] = [
  {
    id: 'u11-l1',
    title: 'Future tense — will',
    popCulture: 'Avengers: Endgame',
    xpReward: 25,
    exercises: [
      {
        id: 'u11-l1-e1',
        type: 'multiple_choice',
        prompt: '"We ___ win." — Cap\'s promise.',
        choices: ['will', 'are', 'do', 'were'],
        answerIndex: 0,
        newWord: 'will',
      },
      {
        id: 'u11-l1-e2',
        type: 'reorder',
        prompt: 'Build: "Tony will make a new suit."',
        tokens: ['Tony', 'will', 'make', 'a', 'new', 'suit', '.'],
        answer: ['Tony', 'will', 'make', 'a', 'new', 'suit', '.'],
      },
      {
        id: 'u11-l1-e3',
        type: 'fill_blank',
        prompt: '"Thanos ___ snap his fingers." (negative)',
        answer: 'will not',
        accepts: ['won\'t', 'will not'],
      },
      {
        id: 'u11-l1-e4',
        type: 'translate',
        prompt: 'Translate: "I\'ll be there in five minutes."',
        from: 'en',
        to: 'pt',
        answer: 'Estarei lá em cinco minutos.',
        accepts: ['Eu estarei lá em cinco minutos.'],
      },
      {
        id: 'u11-l1-e5',
        type: 'speak',
        prompt: 'Make a promise.',
        target: 'I will always help my friends.',
      },
      {
        id: 'u11-l1-e6',
        type: 'listen_choice',
        prompt: 'Which sentence did you hear?',
        audioText: 'We will win, whatever it takes.',
        choices: [
          'We will win, whatever it takes.',
          'We will fight, whatever it takes.',
          'We will go, whatever it takes.',
          'We will stop, whatever it takes.',
        ],
        answerIndex: 0,
      },
    ],
  },
  {
    id: 'u11-l2',
    title: 'Going to — plans',
    popCulture: 'Attack on Titan',
    xpReward: 25,
    exercises: [
      {
        id: 'u11-l2-e1',
        type: 'multiple_choice',
        prompt: '"Eren is ___ fight the titans."',
        choices: ['going to', 'goes to', 'went', 'gone to'],
        answerIndex: 0,
        newWord: 'going to',
      },
      {
        id: 'u11-l2-e2',
        type: 'reorder',
        prompt: 'Build: "Mikasa is going to protect Eren."',
        tokens: ['Mikasa', 'is', 'going', 'to', 'protect', 'Eren', '.'],
        answer: ['Mikasa', 'is', 'going', 'to', 'protect', 'Eren', '.'],
      },
      {
        id: 'u11-l2-e3',
        type: 'translate',
        prompt: 'Translate: "Eu vou estudar hoje à noite."',
        from: 'pt',
        to: 'en',
        answer: 'I am going to study tonight.',
        accepts: ['I\'m going to study tonight.'],
      },
    ],
  },
];

export const U12_LESSONS: Lesson[] = [
  {
    id: 'u12-l1',
    title: 'Conditionals — if',
    popCulture: 'The Matrix',
    xpReward: 30,
    exercises: [
      {
        id: 'u12-l1-e1',
        type: 'multiple_choice',
        prompt: '"If you take the red pill, you ___ the truth."',
        choices: ['will see', 'sees', 'saw', 'have seen'],
        answerIndex: 0,
        newWord: 'will see',
      },
      {
        id: 'u12-l1-e2',
        type: 'fill_blank',
        prompt: '"If Neo ___ (be) The One, humanity is saved."',
        answer: 'is',
      },
      {
        id: 'u12-l1-e3',
        type: 'reorder',
        prompt: 'Build: "If you were there, you would believe."',
        tokens: ['If', 'you', 'were', 'there', ',', 'you', 'would', 'believe', '.'],
        answer: ['If', 'you', 'were', 'there', ',', 'you', 'would', 'believe', '.'],
      },
      {
        id: 'u12-l1-e4',
        type: 'translate',
        prompt: 'Translate: "If I had a choice, I would stay."',
        from: 'en',
        to: 'pt',
        answer: 'Se eu tivesse uma escolha, eu ficaria.',
      },
    ],
  },
  {
    id: 'u12-l2',
    title: 'Work & careers',
    popCulture: 'Peaky Blinders',
    xpReward: 25,
    exercises: [
      {
        id: 'u12-l2-e1',
        type: 'multiple_choice',
        prompt: 'Thomas Shelby is a ___.',
        choices: ['businessman', 'farmer', 'artist', 'doctor'],
        answerIndex: 0,
        newWord: 'businessman',
      },
      {
        id: 'u12-l2-e2',
        type: 'match_pairs',
        prompt: 'Match careers.',
        pairs: [
          { left: 'Doctor', right: 'Médico' },
          { left: 'Teacher', right: 'Professor' },
          { left: 'Lawyer', right: 'Advogado' },
          { left: 'Engineer', right: 'Engenheiro' },
        ],
      },
      {
        id: 'u12-l2-e3',
        type: 'translate',
        prompt: 'Translate: "He works at a bank."',
        from: 'en',
        to: 'pt',
        answer: 'Ele trabalha em um banco.',
        accepts: ['Ele trabalha num banco.'],
      },
    ],
  },
];

export const U13_LESSONS: Lesson[] = [
  {
    id: 'u13-l1',
    title: 'Present perfect',
    popCulture: 'Dark',
    xpReward: 30,
    exercises: [
      {
        id: 'u13-l1-e1',
        type: 'multiple_choice',
        prompt: '"Jonas ___ travelled through time."',
        choices: ['has', 'is', 'does', 'did'],
        answerIndex: 0,
        newWord: 'has',
      },
      {
        id: 'u13-l1-e2',
        type: 'fill_blank',
        prompt: '"The bunker ___ (be) sealed for decades."',
        answer: 'has been',
      },
      {
        id: 'u13-l1-e3',
        type: 'reorder',
        prompt: 'Build: "Martha has never seen this before."',
        tokens: ['Martha', 'has', 'never', 'seen', 'this', 'before', '.'],
        answer: ['Martha', 'has', 'never', 'seen', 'this', 'before', '.'],
      },
      {
        id: 'u13-l1-e4',
        type: 'translate',
        prompt: 'Translate: "I have lived here for three years."',
        from: 'en',
        to: 'pt',
        answer: 'Eu moro aqui há três anos.',
        accepts: ['Moro aqui há três anos.'],
      },
    ],
  },
];

export const U14_LESSONS: Lesson[] = [
  {
    id: 'u14-l1',
    title: 'Modals — can, must, should',
    popCulture: 'Spider-Man',
    xpReward: 25,
    exercises: [
      {
        id: 'u14-l1-e1',
        type: 'multiple_choice',
        prompt: '"With great power ___ come great responsibility."',
        choices: ['must', 'can', 'should', 'may'],
        answerIndex: 0,
        newWord: 'must',
      },
      {
        id: 'u14-l1-e2',
        type: 'fill_blank',
        prompt: '"Peter ___ (can) climb walls."',
        answer: 'can',
      },
      {
        id: 'u14-l1-e3',
        type: 'translate',
        prompt: 'Translate: "Você deveria ligar para sua tia."',
        from: 'pt',
        to: 'en',
        answer: 'You should call your aunt.',
      },
      {
        id: 'u14-l1-e4',
        type: 'reorder',
        prompt: 'Build: "Heroes must protect the innocent."',
        tokens: ['Heroes', 'must', 'protect', 'the', 'innocent', '.'],
        answer: ['Heroes', 'must', 'protect', 'the', 'innocent', '.'],
      },
    ],
  },
];

export const U15_LESSONS: Lesson[] = [
  {
    id: 'u15-l1',
    title: 'Reported speech',
    popCulture: 'Black Mirror',
    xpReward: 30,
    exercises: [
      {
        id: 'u15-l1-e1',
        type: 'multiple_choice',
        prompt: 'He said: "I am tired" → He said ___ tired.',
        choices: ['he was', 'he is', 'he will', 'he has'],
        answerIndex: 0,
        newWord: 'reported',
      },
      {
        id: 'u15-l1-e2',
        type: 'fill_blank',
        prompt: 'She said: "I will come." → She said she ___ come.',
        answer: 'would',
      },
      {
        id: 'u15-l1-e3',
        type: 'translate',
        prompt: 'Translate: "Ele disse que estava com medo."',
        from: 'pt',
        to: 'en',
        answer: 'He said he was afraid.',
      },
    ],
  },
];
